import { useEffect } from 'react'
import { Link, Navigate, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Header, Footer } from '@/components/layout'
import { Contact } from '@/components/sections/Contact'
import { PortfolioIcon } from '@/components/ui/PortfolioIcon'
import type { TechnicalArticle } from '@/components/sections/Writing'
import { ArticleDiagram } from '@/components/ui/ArticleDiagram'
import { articleMedia } from '@/content/articleMedia'
import { legacyArticleSlugs } from '@/content/sitePaths'

export default function WritingArticle() {
  const { slug } = useParams()
  const { t } = useTranslation()
  const homeUrl = '/'
  const articles = t('portfolio.articles', {
    returnObjects: true,
  }) as TechnicalArticle[]
  const article = articles.find((item) => item.slug === slug)
  const media = article && articleMedia[article.slug]
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [slug])
  if (slug && legacyArticleSlugs[slug]) {
    return <Navigate replace to={`/writing/${legacyArticleSlugs[slug]}`} />
  }
  return (
    <div className="portfolio-site">
      <Header />
      <main id="main-content" className="article-page">
        <article className="article-shell">
          <Link className="text-link back-link" to={`${homeUrl}#writing`}>
            <PortfolioIcon name="arrow" />
            {t('portfolio.writing.back')}
          </Link>
          {!article && (
            <>
              <h1>{t('portfolio.writing.notFound')}</h1>
              <p className="article-lead">
                {t('portfolio.writing.notFoundDescription')}
              </p>
            </>
          )}
          {article && (
            <>
              <p className="eyebrow">
                {article.category} · {article.reading}
              </p>
              <h1>{article.title}</h1>
              <p className="article-lead">{article.intro}</p>
              <div className="article-byline">
                <img
                  src="/images/marcos-aquino.jpg"
                  alt=""
                  width="40"
                  height="40"
                />
                <span><Link to={`${homeUrl}#about`}>Marcos Aquino</Link> · Embedded systems / IoT / Cloud</span>
              </div>
              {media && (
                <figure className="article-cover">
                  <img
                    src={media.src}
                    alt={article.image.alt}
                    fetchPriority="high"
                  />
                  <figcaption>{article.image.caption}</figcaption>
                </figure>
              )}
              <div className="article-body">
                {article.sections.map((section, index) => (
                  <section key={section.title}>
                    <h2>{section.title}</h2>
                    {section.paragraphs.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                    {section.table && (
                      <div
                        className="article-table-wrap"
                        role="region"
                        aria-label={section.table.caption}
                        tabIndex={0}
                      >
                        <table>
                          <caption>{section.table.caption}</caption>
                          <thead>
                            <tr>
                              {section.table.headers.map((header) => (
                                <th scope="col" key={header}>
                                  {header}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {section.table.rows.map((row) => (
                              <tr key={row[0]}>
                                {row.map((cell, cellIndex) =>
                                  cellIndex === 0 ? (
                                    <th scope="row" key={cell}>
                                      {cell}
                                    </th>
                                  ) : (
                                    <td key={cell}>{cell}</td>
                                  ),
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {section.links && (
                      <p className="article-source-links">
                        {section.links.map((link) => (
                          <a
                            key={link.href}
                            href={link.href}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {link.label}
                            <PortfolioIcon name="arrow" />
                          </a>
                        ))}
                      </p>
                    )}
                    {index === 1 && media && (
                      <ArticleDiagram
                        content={article.diagram}
                        icon={media.icon}
                      />
                    )}
                  </section>
                ))}
              </div>
              <aside className="related-articles">
                <h2>{t('portfolio.writing.related')}</h2>
                {articles
                  .filter((item) => item.slug !== slug)
                  .map((item) => (
                    <Link to={`${homeUrl}writing/${item.slug}`} key={item.slug}>
                      {item.title}
                      <PortfolioIcon name="arrow" />
                    </Link>
                  ))}
              </aside>
            </>
          )}
        </article>
        <Contact />
      </main>
      <Footer />
    </div>
  )
}
